// noinspection JSUnusedGlobalSymbols

import React from "react";
import {useRef, useCallback, useSyncExternalStore} from 'react';
import {BehaviorSubject} from 'rxjs';
import {clsx, type ClassValue} from "clsx";
import {twMerge} from "tailwind-merge";

const MOBILE_BREAKPOINT = 768;

export function useDebounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T {
  const timeoutRef = useRef<NodeJS.Timeout>();

  return useCallback((...args: Parameters<T>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      func(...args);
    }, delay);
  }, [func, delay]) as T;
}

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return !!isMobile
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function useMergeRefs<T = any>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(node);
      } else {
        (ref as React.MutableRefObject<T | null>).current = node;
      }
    });
  };
}

export function useRxSync<T>(subject: BehaviorSubject<T>): T {
  return useSyncExternalStore(
    (callback) => {
      const sub = subject.subscribe(callback);
      return () => sub.unsubscribe();
    },
    () => subject.value
  );
}
